import { AppError } from '@telestore/shared';

/**
 * Deep links. These are the mechanism behind the acquisition funnel in
 * CLAUDE.md §6.3: an ad-bought customer checks out on the plain web, then one
 * tap on "Get order updates on Telegram" converts them into a free lifetime
 * bot subscriber.
 *
 * Telegram's `start` payload allows only [A-Za-z0-9_-] and 64 characters, so
 * every payload is `kind_value` with a short kind prefix.
 */

export type DeepLinkKind = 'order' | 'promo' | 'ref' | 'invite' | 'shop' | 'product' | 'provision';

const KIND_PREFIX: Record<DeepLinkKind, string> = {
  order: 'o',
  promo: 'p',
  ref: 'r',
  invite: 'i',
  shop: 's',
  product: 'd',
  provision: 'b',
};

const PREFIX_KIND = Object.fromEntries(
  Object.entries(KIND_PREFIX).map(([kind, prefix]) => [prefix, kind as DeepLinkKind]),
) as Record<string, DeepLinkKind>;

const PAYLOAD_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function encodeStartPayload(kind: DeepLinkKind, value: string): string {
  const payload = `${KIND_PREFIX[kind]}_${value}`;
  if (!PAYLOAD_RE.test(payload)) {
    throw new AppError('validation_failed', `Deep link payload is not Telegram-safe: ${payload}`);
  }
  return payload;
}

export interface ParsedStartPayload {
  kind: DeepLinkKind;
  value: string;
}

export function parseStartPayload(payload: string | undefined | null): ParsedStartPayload | null {
  if (!payload || !PAYLOAD_RE.test(payload)) return null;
  const separator = payload.indexOf('_');
  if (separator < 1) return null;
  const kind = PREFIX_KIND[payload.slice(0, separator)];
  const value = payload.slice(separator + 1);
  if (!kind || value === '') return null;
  return { kind, value };
}

export function botDeepLink(botUsername: string, kind: DeepLinkKind, value: string): string {
  return `https://t.me/${botUsername}?start=${encodeStartPayload(kind, value)}`;
}

/** Opens the Mini App directly rather than the bot chat. */
export function miniAppDeepLink(
  botUsername: string,
  appShortName: string,
  kind: DeepLinkKind,
  value: string,
): string {
  return `https://t.me/${botUsername}/${appShortName}?startapp=${encodeStartPayload(kind, value)}`;
}

/** "Chat with shop" (§2) — the merchant's own account, not a bot we run. */
export function merchantChatLink(username: string): string {
  return `https://t.me/${username.replace(/^@/, '')}`;
}

/**
 * Storefront URLs. Product links get their own path because §7 injects
 * per-product OG meta tags there — link previews in Telegram and Facebook ARE
 * the distribution model.
 */
export function storefrontUrl(
  baseUrl: string,
  shopSlug: string,
  options: { productSlug?: string; src?: string; ref?: string; promo?: string } = {},
): string {
  const path = options.productSlug
    ? `/s/${shopSlug}/p/${options.productSlug}`
    : `/s/${shopSlug}`;
  const url = new URL(path, baseUrl);
  if (options.src) url.searchParams.set('src', options.src);
  if (options.ref) url.searchParams.set('ref', options.ref);
  // §3 `promotion`: a shared link auto-applies the code, so a merchant pastes
  // one link into Facebook instead of explaining a code in every reply.
  if (options.promo) url.searchParams.set('promo', options.promo);
  return url.toString();
}

/* ------------------------------------------------------------------ */
/* Managed Bots (Bot API 10.0)                                         */
/* ------------------------------------------------------------------ */

/**
 * Telegram's own bot-creation link:
 *
 *   https://t.me/newbot/{manager_bot_username}/{suggested_bot_username}[?name=…]
 *
 * Note what this is NOT: it is not a `?start=` payload, so none of the encoding
 * above applies. The two usernames are path segments, which is why the manager
 * bot's username has to be configured rather than derived from its token at
 * call time.
 *
 * Tapping it opens Telegram's own create-a-bot UI with the fields prefilled.
 * The merchant never sees a token and never opens @BotFather; the token comes
 * back to us over the `managed_bot` update instead (§3 `bot-provisioning`).
 */
export function newManagedBotLink(input: {
  managerBotUsername: string;
  suggestedUsername: string;
  suggestedName?: string;
}): string {
  const manager = input.managerBotUsername.replace(/^@/, '');
  const suggested = input.suggestedUsername.replace(/^@/, '');

  if (!isValidBotUsername(suggested)) {
    throw new AppError(
      'validation_failed',
      `Suggested bot username is not valid for Telegram: ${suggested}`,
    );
  }

  const url = `https://t.me/newbot/${manager}/${suggested}`;
  // `name` is the display name, which unlike the username may be anything.
  return input.suggestedName
    ? `${url}?name=${encodeURIComponent(input.suggestedName)}`
    : url;
}

/** Telegram's rule: 5–32 of [A-Za-z0-9_], and it must end in "bot". */
export function isValidBotUsername(username: string): boolean {
  const value = username.replace(/^@/, '');
  return /^[A-Za-z0-9_]{5,32}$/.test(value) && /bot$/i.test(value);
}

/**
 * Turn a shop slug into a username Telegram will accept.
 *
 * Hyphens are legal in our slugs and illegal in Telegram usernames, so they
 * become underscores. The suffix is added only when it is missing, so
 * "mekong-coffee" becomes "mekong_coffee_bot" rather than "…bot_bot".
 *
 * This is a SUGGESTION: Telegram checks availability when the merchant lands
 * on the creation screen, and they can type over it. We deliberately do not
 * try to guarantee uniqueness here — losing a race against another merchant's
 * signup would be a worse failure than an occupied name the merchant can see
 * and fix on the spot.
 */
export function suggestBotUsername(slug: string): string {
  const base = slug.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const withSuffix = /bot$/.test(base) ? base : `${base}_bot`;

  // Trim from the FRONT of the stem so the "bot" suffix always survives.
  if (withSuffix.length <= 32) return withSuffix.padEnd(5, '0');
  return `${withSuffix.slice(0, 32 - 4).replace(/_+$/, '')}_bot`;
}
